// „Ember als App installieren" — Bottom-Sheet für den PWA-Install-Flow.
//
// iOS: 2-Schritt-Anleitung (Teilen → Zum Home-Bildschirm), da Apple keinen
// programmatischen Install-Prompt anbietet. Android/Chromium: Button, der
// das in pwa-install.ts abgefangene beforeinstallprompt-Event abfeuert.
// Desktop/standalone rendert nichts (Plattform 'none').

import { Share, Smartphone, SquarePlus } from 'lucide-react'
import { type ReactNode, useEffect, useState, useSyncExternalStore } from 'react'
import { isIOS } from '../lib/notifications'
import {
  detectInstallPlatform,
  type InstallPlatform,
  isNonSafariIOSBrowser,
  markInstallSheetDismissed,
  promptInstall,
  subscribeInstallPromptChange,
} from '../lib/pwa-install'
import { useIsMobile } from '../lib/use-media-query'
import { Button } from './ui/button'
import { Dialog } from './ui/dialog'

/**
 * Reaktive Install-Plattform: reagiert auf den Phone-Breakpoint und auf ein
 * (auch später) eintreffendes beforeinstallprompt. Standalone/iOS sind pro
 * Page-Load statisch — kein Subscribe nötig.
 */
export function usePwaInstallPlatform(): InstallPlatform {
  const isPhone = useIsMobile()
  // useSyncExternalStore, damit ein nach dem Mount eintreffendes
  // beforeinstallprompt den Menüpunkt/Button nachträglich einblendet.
  useSyncExternalStore(
    subscribeInstallPromptChange,
    () => (typeof window === 'undefined' ? false : detectInstallPlatform(isPhone) !== 'none'),
    () => false,
  )
  if (typeof window === 'undefined') return 'none'
  return detectInstallPlatform(isPhone)
}

interface PwaInstallSheetProps {
  open: boolean
  onClose: () => void
}

export function PwaInstallSheet({ open, onClose }: PwaInstallSheetProps) {
  const platform = usePwaInstallPlatform()
  const [installing, setInstalling] = useState(false)

  // Erfolgreiche Installation (appinstalled) schließt das Sheet von selbst.
  useEffect(() => {
    if (!open) return
    const handler = () => onClose()
    window.addEventListener('appinstalled', handler)
    return () => window.removeEventListener('appinstalled', handler)
  }, [open, onClose])

  // Jede Art von Schließen (Später / Drag-down / Backdrop / X) zählt als
  // Dismiss → Timestamp für die Snooze-Frist der Auto-Anzeige.
  const dismiss = () => {
    markInstallSheetDismissed()
    onClose()
  }

  if (platform === 'none') return null

  const onAndroidInstall = async () => {
    setInstalling(true)
    const outcome = await promptInstall()
    setInstalling(false)
    // 'accepted' → appinstalled schließt gleich; 'dismissed' → User hat den
    // nativen Prompt weggetippt, unser Sheet zumachen und snoozen.
    if (outcome !== 'accepted') dismiss()
  }

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      title="Ember als App installieren"
      description="Vollbild ohne Browser-Leiste, Push-Benachrichtigungen und ein eigenes Icon auf dem Home-Bildschirm."
      footer={
        platform === 'android' ? (
          <>
            <Button variant="ghost" onClick={dismiss}>
              Später
            </Button>
            <Button onClick={onAndroidInstall} disabled={installing}>
              <Smartphone className="h-4 w-4" />
              App installieren
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={dismiss} className="w-full sm:w-auto">
            Später
          </Button>
        )
      }
    >
      {platform === 'ios' ? <IosInstallSteps /> : <AndroidInstallBody />}
    </Dialog>
  )
}

function IosInstallSteps() {
  const inThirdPartyBrowser =
    typeof navigator !== 'undefined' && isIOS() && isNonSafariIOSBrowser(navigator.userAgent)
  return (
    <div className="space-y-4 pb-2">
      <InstallStep
        step={1}
        icon={<Share className="h-4 w-4" />}
        title="Teilen antippen"
        text={
          <>
            Unten in der Safari-Leiste das <span className="text-text-primary">Teilen</span>
            -Symbol antippen.
          </>
        }
      />
      <InstallStep
        step={2}
        icon={<SquarePlus className="h-4 w-4" />}
        title="Zum Home-Bildschirm"
        text={
          <>
            Im Menü <span className="text-text-primary">„Zum Home-Bildschirm“</span> wählen und mit
            „Hinzufügen“ bestätigen.
          </>
        }
      />
      {inThirdPartyBrowser && (
        <p className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-3.5 py-2.5 text-xs text-text-tertiary">
          Sieht so aus, als wärst du nicht in Safari. Öffne diese Seite in Safari — nur dort gibt es
          „Zum Home-Bildschirm“.
        </p>
      )}
    </div>
  )
}

function InstallStep({
  step,
  icon,
  title,
  text,
}: {
  step: number
  icon: ReactNode
  title: string
  text: ReactNode
}) {
  return (
    <div className="flex items-start gap-3.5">
      <div className="relative shrink-0">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/15 text-accent">
          {icon}
        </div>
        <span className="absolute -left-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-2xs font-semibold leading-none text-bg-base tabular-nums">
          {step}
        </span>
      </div>
      <div className="min-w-0 pt-0.5">
        <p className="text-sm font-medium text-text-primary">{title}</p>
        <p className="mt-0.5 text-sm text-text-secondary">{text}</p>
      </div>
    </div>
  )
}

function AndroidInstallBody() {
  return (
    <div className="space-y-3 pb-2">
      <div className="flex items-start gap-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
          <Smartphone className="h-4 w-4" />
        </div>
        <p className="pt-0.5 text-sm text-text-secondary">
          Ein Tipp auf <span className="text-text-primary">„App installieren“</span> öffnet den
          Install-Dialog deines Browsers — danach startet Ember direkt vom Home-Bildschirm.
        </p>
      </div>
    </div>
  )
}
